<template>
  <div class="flex items-center gap-2 text-xl">
    <TextMultiline
      :text="i18n.t('sidebar.applications')"
      class="cursor-pointer"
      @click="goToApplications" />
    <p>/</p>
    <SmSkeleton v-if="loading" width="100px" />
    <div v-else-if="application" class="flex items-center gap-2 font-bold">
      <ApplicationIcon
        v-if="!isSSO"
        :application-name="application.software_name" />
      <TextMultiline :text="internalName" />
    </div>
    <p v-else>
      {{ i18n.t('editApplication') }}
    </p>
  </div>
</template>

<script setup lang="ts">
  import { SoftwareOut } from '@/client'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'
  import type { LocationQueryRaw } from 'vue-router'

  const i18n = useI18n()
  const route = useRoute()
  const router = useRouter()

  const application = ref<SoftwareOut>()
  const loading = ref(true)

  const isSSO = computed(() => route.query.sso === 'true')
  const subAccountID = computed(() =>
    decodeURIComponent(route.params.subAccountId as string)
  )

  const internalName = computed(() => {
    if (!application.value) return

    if (isSSO.value) {
      return application.value.sub_accounts[subAccountID.value].name
    } else if (application.value?.display_name) {
      return application.value?.display_name
    }
  })

  onMounted(() => {
    loadApplication()
  })

  const goToApplications = () => {
    const historyState = window.history.state as { back?: string } | null
    const backLocation = historyState?.back

    if (backLocation) {
      const resolvedBackLocation = router.resolve(backLocation)

      if (resolvedBackLocation.name === 'Applications') {
        const nextQuery = {
          ...resolvedBackLocation.query,
        } as LocationQueryRaw

        if (!isSSO.value && application.value?._id) {
          nextQuery.scroll = application.value._id
        } else {
          delete nextQuery.scroll
        }

        router.push({
          name: 'Applications',
          query: nextQuery,
        })
        return
      }
    }

    router.push({
      name: 'Applications',
      query:
        !isSSO.value && application.value?._id
          ? { scroll: application.value._id }
          : undefined,
    })
  }

  function loadApplication() {
    const applicationId = route.params.id as string
    ApplicationStore.getApplicationById(applicationId)
      .then((app) => {
        application.value = app ?? undefined
      })
      .catch((error) => {
        console.error('Error loading application:', error)
      })
      .finally(() => {
        loading.value = false
      })
  }
</script>

<style scoped lang="scss"></style>
